import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Firm-controlled automation settings.
 *
 * Court LOR auto-send used to be an environment variable, which meant only the developers could
 * change it. Lloyd asked for the switch himself (Jul 18 2026), so it lives in the database and is
 * flipped here by any manager holding "Can manage firm settings".
 *
 * The unusable-email count sits next to the switch deliberately. Auto-send is only safe once court
 * contact data is clean, and that number is the honest measure of how clean it currently is.
 */

interface AutomationSettings {
  courtLorAutoSend: boolean;
  appearanceAutoNotify: boolean;
  bondCourtAutoSend: boolean;
  /*
   * Everything below is optional, and the fields above are not.
   *
   * They were all added on 8 Sep, and an API that has not been restarted or is mid-rolling-deploy
   * answers without them. Typed as required, `settings.discoveryApproval.approved` then throws and
   * takes the whole settings page down over a section the reader may not even care about. Optional
   * here, guarded at every use, and a missing field simply hides its control.
   */
  portalInviteSend?: boolean;
  messagesClientResend?: boolean;
  /** Blank means the shipped sentence is in use. */
  messagesResendPreface?: string;
  messagesResendPrefaceDefault?: string;
  /** The 39.14 wording this build carries, and whether the firm has signed it off. */
  discoveryApproval?: {
    approved: boolean;
    currentRevision: number;
    approvedRevision: number | null;
    explanation: string;
  };
  canManage: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  courtsWithUnusableEmail: number;
  courtsTotal: number;
}

const ENDPOINT = (process.env.API_HOST || '') + '/api/v1/manager/settings/automation';

const AutomationSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<AutomationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(ENDPOINT, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to load automation settings');
      }
      setSettings((await response.json()) as AutomationSettings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load automation settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * The wording is a text box, so it is held locally while it is being typed and written on blur.
   * A switch can save on every change because there is one keystroke; a sentence cannot, or every
   * character is a request and a half-typed line is briefly what the client would receive.
   */
  const [preface, setPreface] = useState('');
  useEffect(() => {
    if (settings) setPreface(settings.messagesResendPreface ?? '');
  }, [settings?.messagesResendPreface]);

  /*
   * Approving the 39.14 wording. Not a switch, and deliberately not undoable from here.
   *
   * The revision is sent explicitly rather than "approve whatever is current", so a person who read
   * the paragraphs on this page cannot accidentally sign off a newer deploy. The server refuses a
   * mismatch with a 409.
   */
  const approveDiscoveryWording = async () => {
    if (!settings?.discoveryApproval) return;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approveDiscoveryRevision: settings.discoveryApproval?.currentRevision,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to approve the wording');
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve the wording');
    } finally {
      setSaving(false);
    }
  };

  const savePreface = async () => {
    if (!settings) return;
    const next = preface.trim();
    if (next === (settings.messagesResendPreface ?? '').trim()) return;

    setSaving(true);
    setError(null);
    try {
      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messagesResendPreface: next }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to save the wording');
      }
      await load();
    } catch (err) {
      // Put the stored value back. A box still showing what somebody typed, after the save failed,
      // reads as saved.
      setPreface(settings.messagesResendPreface ?? '');
      setError(err instanceof Error ? err.message : 'Failed to save the wording');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (
    key:
      | 'courtLorAutoSend'
      | 'appearanceAutoNotify'
      | 'bondCourtAutoSend'
      | 'portalInviteSend'
      | 'messagesClientResend',
    nextValue: boolean,
  ) => {
    if (!settings) return;

    // Optimistic, but reverted on failure — a switch that lies about whether mail is going out
    // is worse than one that is briefly slow.
    const previous = settings[key];
    setSettings({ ...settings, [key]: nextValue });
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: nextValue }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to update automation settings');
      }
      await load();
    } catch (err) {
      setSettings({ ...settings, [key]: previous });
      setError(err instanceof Error ? err.message : 'Failed to update automation settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Automation
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !settings ? (
        <CircularProgress size={24} />
      ) : settings ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.courtLorAutoSend}
                onChange={(e) => void handleToggle('courtLorAutoSend', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Send letters of representation to courts automatically on approval"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.courtLorAutoSend
              ? 'Approved LORs are sent straight to courts that have a usable email address. Courts without one still route to the internal LOR mailbox.'
              : 'Approved LORs are saved as Outlook drafts for staff to review and send by hand.'}
          </Typography>

          {settings.courtsWithUnusableEmail > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {settings.courtsWithUnusableEmail} of {settings.courtsTotal} courts have a missing or
              placeholder email address. Their letters route to the internal LOR mailbox rather than
              to the court.
              <br />
              Note that this check cannot detect an address that is well-formed but simply wrong —
              those will be sent while auto-send is on.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Lloyd, Aug 10: a notice saying the client must appear should mark the case and email
              them. Shipped off — the detection is a model reading a scan, and the output tells
              somebody to attend court. Off still records and still shows; it just does not send. */}
          <FormControlLabel
            control={
              <Switch
                checked={settings.appearanceAutoNotify}
                onChange={(e) => void handleToggle('appearanceAutoNotify', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Email clients automatically when a scanned notice says they must attend court"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.appearanceAutoNotify
              ? 'When a pre-trial or other notice clearly states the client must attend, the case is marked and the client is emailed and texted without anyone reviewing it first.'
              : 'Notices are still read and the case is still marked, but nobody is emailed until a staff member confirms it on the case. Staff ticking it by hand always emails the client, whichever way this is set.'}
          </Typography>

          {!settings.appearanceAutoNotify && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Recommended while this is new. Watch what the notices are being read as for a week or
              two on the case screens, then turn this on once you are satisfied.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Lloyd, Aug 1: "build all things on a draft basis for now with the ability to switch
              to automatic at a later date." A person still marks the bond either way — this only
              decides whether the prepared message leaves without a second person seeing it. */}
          <FormControlLabel
            control={
              <Switch
                checked={settings.bondCourtAutoSend}
                onChange={(e) => void handleToggle('bondCourtAutoSend', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Send bonds to courts automatically once a reviewer marks them"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.bondCourtAutoSend
              ? 'A bond a reviewer marks is emailed straight to the court, if that court has a usable email address. Nobody sees it before it goes.'
              : 'A bond a reviewer marks is prepared, addressed and attached, then waits in the Court Communication drafts for someone to send. Marking a bond is always a person’s decision, whichever way this is set.'}
          </Typography>

          {!settings.bondCourtAutoSend && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Recommended until you have watched a few go out. Whichever way this is set, a court
              with no usable email address gets nothing — those route to the internal LOR mailbox
              and still need sending by hand.
            </Alert>
          )}

          {/* Both of these existed as settings with nothing able to flip them, so turning either
              on meant a hand-written row in app_setting.

              Hidden rather than shown as "off" when the API does not report them. An unchecked
              switch is a claim that sending is disabled, and an older API answering without the
              field is not evidence of that. Absent means unknown, and a control that states the
              wrong thing about client-facing sends is worse than one that is not there. */}
          {settings.portalInviteSend !== undefined && (
          <>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={settings.portalInviteSend === true}
                onChange={(e) => void handleToggle('portalInviteSend', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Send portal invitations to existing clients"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.portalInviteSend
              ? 'The invitation script may send. It is the highest-volume client-facing send in the system, so run it as a dry run first and read the list.'
              : 'The invitation script refuses to send and reports what it would have done. Turn this on only once the wording has been approved.'}
          </Typography>
          </>
          )}

          {settings.messagesClientResend !== undefined && (
          <>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={settings.messagesClientResend === true}
                onChange={(e) => void handleToggle('messagesClientResend', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Send an unanswered message to the client again"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.messagesClientResend
              ? 'A message the client has not answered is sent to them again after 24 hours, then 48, then weekly, by portal, email and text, until they reply or somebody closes the conversation.'
              : 'Nothing is sent to clients. Conversations still come back to the Messages tab on the same schedule for staff to chase by hand.'}
          </Typography>
          </>
          )}

          {/* The sentence that goes on top of the client's own message, which then follows
              verbatim. Kept as a setting so changing it is not a release. Left blank, the shipped
              sentence is used, which is why the default is the placeholder rather than the value. */}
          {settings.messagesResendPrefaceDefault !== undefined && (
          <TextField
            fullWidth
            multiline
            size="small"
            label="Line on top of a re-sent message"
            placeholder={settings.messagesResendPrefaceDefault}
            value={preface}
            onChange={(e) => setPreface(e.target.value)}
            onBlur={() => void savePreface()}
            disabled={!settings.canManage || saving}
            inputProps={{ maxLength: 200 }}
            helperText={
              preface.trim()
                ? `${preface.trim().length} of 200 characters. The client's own message follows underneath.`
                : `Blank, so this is used: "${settings.messagesResendPrefaceDefault}"`
            }
          />
          )}

          {/* The discovery wording. Shown only when something is waiting, because an approval
              nobody needs to give is a control staff learn to ignore. */}
          {settings.discoveryApproval && !settings.discoveryApproval.approved && (
            <Alert
              severity="warning"
              action={
                settings.canManage ? (
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => void approveDiscoveryWording()}
                  >
                    Approve
                  </Button>
                ) : undefined
              }
            >
              {settings.discoveryApproval.explanation} Read the request before approving: the
              wording is the statutory basis of a document served on a court, and until it is
              approved the 39.14 request is left off the not-guilty letter. The letter and the
              preservation demand still go out.
            </Alert>
          )}

          {!settings.canManage && (
            <Typography variant="body2" color="text.secondary">
              You do not have permission to change this. Ask a manager with settings access.
            </Typography>
          )}

          {settings.updatedAt && (
            <Typography variant="caption" color="text.secondary">
              Court auto-send last changed
              {settings.updatedBy ? ` by ${settings.updatedBy}` : ''} on{' '}
              {new Date(settings.updatedAt).toLocaleString()}
            </Typography>
          )}
        </Box>
      ) : null}
    </Paper>
  );
};

export default AutomationSettingsPanel;
